import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import {
  WorkStepDriftItem,
  WorkStepEditedSinceSessionStart,
} from "../../../models/audit/collabWorkStepSession";

interface RefineOverwriteConfirmDialogProps {
  conflict: WorkStepEditedSinceSessionStart | null;
  isFinalizing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const itemLabel = (item: WorkStepDriftItem): string => {
  switch (item.kind) {
    case "work_step":
      return "Work step name and description";
    case "attribute":
      return `Attribute ${item.label}`;
    case "testing_table":
      return `Testing table "${item.label}"`;
  }
};

function ChangeGroup({
  heading,
  items,
}: {
  heading: string;
  items: WorkStepDriftItem[];
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-sm font-medium text-stone-900">{heading}</p>
      <ul className="mt-1 list-disc pl-5 text-sm text-stone-600">
        {items.map((item) => (
          <li key={`${item.kind}-${item.id ?? item.label}`}>
            {itemLabel(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Shown when finalizing a refine session would overwrite edits made to the
// work step after the session began. Last finalize wins, so the user chooses
// knowingly: every overwritten edit is listed, and so are the test runs that
// go with any testing table added since.
export function RefineOverwriteConfirmDialog({
  conflict,
  isFinalizing,
  onConfirm,
  onCancel,
}: RefineOverwriteConfirmDialogProps) {
  const testRuns = conflict?.test_runs_at_risk ?? 0;
  return (
    <AlertDialog
      open={conflict !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            This work step changed since you started refining
          </AlertDialogTitle>
          <AlertDialogDescription>
            The work step was edited after this refine session began. Finalizing
            applies your refined plan as it stands and overwrites those edits.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {conflict && (
          <div className="space-y-3">
            <ChangeGroup
              heading="Added since — will be deleted"
              items={conflict.changes.added}
            />
            <ChangeGroup
              heading="Removed since — will be restored"
              items={conflict.changes.removed}
            />
            <ChangeGroup
              heading="Edited since — will be reset to your plan"
              items={conflict.changes.edited}
            />
            {testRuns > 0 && (
              <p className="text-sm font-medium text-red-600">
                Deleting the added testing tables also deletes {testRuns} test{" "}
                {testRuns === 1 ? "run" : "runs"}.
              </p>
            )}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isFinalizing}>
            Keep refining
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={isFinalizing}
          >
            Overwrite and finalize
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
